/**
 * DockedCollapsible — Render-style persistent-header collapsible.
 *
 * Authority: docs/contracts/DESIGN_IMPLEMENTATION_CONTRACT.md Section 3.1 / 4.1.
 * Composes:  ui/collapsible.jsx (shadcn Collapsible).
 * Tokens:    DS_MOTION.collapsible (240ms, cubic-bezier(0.22, 1, 0.36, 1)),
 *            DS_COLORS, DS_SPACING, DS_TYPOGRAPHY.
 *
 * Difference from plain shadcn Collapsible:
 *   - The header is ALWAYS visible (docked) regardless of expanded state.
 *   - The body transitions via grid-template-rows 0fr → 1fr + opacity —
 *     never display:none, never height animation — per DESIGN_IMPLEMENTATION
 *     _CONTRACT.md Section 4.1.
 *   - Header shows a chevron that rotates on open.
 *
 * Props:
 *   title        — required string (passed from copy.js).
 *   chip         — optional React node rendered right of title (e.g. signal pill).
 *   defaultOpen  — bool.
 *   open         — controlled bool.
 *   onOpenChange — controlled setter.
 *   children     — body content.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import {
  DS_BORDERS,
  DS_COLORS,
  DS_MOTION,
  DS_SPACING,
  DS_TYPOGRAPHY,
} from '@/config/ui-invariants';

export function DockedCollapsible({
  title,
  chip,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  children,
}) {
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next) => {
    if (!isControlled) setUncontrolledOpen(next);
    if (onOpenChange) onOpenChange(next);
  };

  const transition = `${DS_MOTION.collapsible.durationMs}ms ${DS_MOTION.collapsible.ease}`;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: DS_SPACING.buttonGap,
            padding: `${DS_SPACING.cardPadding / 2}px ${DS_SPACING.cardPadding}px`,
            background: DS_COLORS.surface.white,
            border: `${DS_BORDERS.width} solid ${DS_BORDERS.color}`,
            borderRadius: DS_BORDERS.radiusCard,
            cursor: 'pointer',
            minHeight: DS_SPACING.minTouchTarget,
            ...DS_TYPOGRAPHY.listItemTitle,
            color: DS_COLORS.text.primary,
            textAlign: 'left',
          }}
        >
          <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </span>
          {chip && <span style={{ flexShrink: 0 }}>{chip}</span>}
          <ChevronDown
            size={20}
            aria-hidden
            style={{
              flexShrink: 0,
              color: DS_COLORS.text.secondary,
              transition: `transform ${transition}`,
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent
        style={{
          display: 'grid',
          gridTemplateRows: open ? '1fr' : '0fr',
          opacity: open ? 1 : 0,
          transition: `grid-template-rows ${transition}, opacity ${transition}`,
          overflow: 'hidden',
        }}
      >
        <div style={{ minHeight: 0, padding: `${DS_SPACING.cardPadding}px 0 0 0` }}>
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

DockedCollapsible.propTypes = {
  title: PropTypes.string.isRequired,
  chip: PropTypes.node,
  defaultOpen: PropTypes.bool,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  children: PropTypes.node,
};

export default DockedCollapsible;
